/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * MINIMUM ORDER QUANTITY (MOQ) — CHECKOUT GATE
 * ------------------------------------------------------------------------------------------------------------
 *
 * Intercepts checkout form submissions and link clicks when the cart total
 * is below the configured minimum. Shows a native <dialog> modal instead.
 * Settings are exposed via window.themeVariables.settings.
 */

(function () {
  const s = window.themeVariables?.settings;
  if (!s?.cartMoqEnabled) return;

  // Parse MOQ amount (supports multi-currency: "USD:5000,EUR:6000")
  function getMoqCents() {
    const raw = String(s.cartMoqAmount || '0').trim();
    if (raw.includes(':')) {
      const currency = s.cartCurrency;
      const pairs = raw.split(',');
      for (const pair of pairs) {
        const [code, val] = pair.trim().split(':');
        if (code.trim().toUpperCase() === currency) return parseInt(val.trim(), 10) || 0;
      }
      // Fallback: first value if no currency match
      const [, val] = pairs[0].trim().split(':');
      return parseInt(val.trim(), 10) || 0;
    }
    return parseInt(raw, 10) || 0;
  }

  let currentCartTotal = s.cartTotalPrice || 0;
  let currentItemCount = s.cartItemCount || 0;
  const moqCents = getMoqCents();
  const moqType = s.cartMoqType || 'amount';
  const moqQuantity = parseInt(s.cartMoqQuantity, 10) || 1;

  // Keep cart state in sync on updates
  document.addEventListener('cart:updated', (e) => {
    if (e.detail?.cart?.total_price != null) {
      currentCartTotal = e.detail.cart.total_price;
    }
    if (e.detail?.cart?.item_count != null) {
      currentItemCount = e.detail.cart.item_count;
    }
  });

  function isBelowMoq() {
    const belowAmount = currentCartTotal < moqCents;
    const belowQuantity = currentItemCount < moqQuantity;

    if (moqType === 'amount') return belowAmount;
    if (moqType === 'quantity') return belowQuantity;
    // "both" — must meet both requirements
    return belowAmount || belowQuantity;
  }

  function showMoqModal() {
    const dialog = document.getElementById('lh-moq-modal');
    if (!dialog) return;
    dialog.removeAttribute('data-closing');
    dialog.showModal();
  }

  function closeMoqModal() {
    const dialog = document.getElementById('lh-moq-modal');
    if (!dialog || !dialog.open) return;
    dialog.setAttribute('data-closing', '');
    dialog.addEventListener('animationend', function handler() {
      dialog.removeEventListener('animationend', handler);
      dialog.removeAttribute('data-closing');
      dialog.close();
    });
  }

  // Close button handlers
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-moq-close]')) {
      closeMoqModal();
    }
  });

  // Close on backdrop click
  document.addEventListener('click', (e) => {
    const dialog = document.getElementById('lh-moq-modal');
    if (e.target === dialog) closeMoqModal();
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const dialog = document.getElementById('lh-moq-modal');
      if (dialog?.open) {
        e.preventDefault();
        closeMoqModal();
      }
    }
  });

  // Intercept form submissions (checkout buttons)
  document.addEventListener('submit', (e) => {
    const form = e.target;
    // Check if this is a cart form submitting to checkout
    const hasCheckoutInput = form.querySelector('input[name="checkout"]') || form.querySelector('button[name="checkout"]');
    if (!hasCheckoutInput) return;

    if (isBelowMoq()) {
      e.preventDefault();
      e.stopPropagation();
      showMoqModal();
    }
  }, true); // capture phase to intercept before theme JS

  // Intercept direct /checkout link clicks
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/checkout"]');
    if (!link) return;

    if (isBelowMoq()) {
      e.preventDefault();
      e.stopPropagation();
      showMoqModal();
    }
  }, true);
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * SLYDE CAROUSEL INTEGRATION
 * ------------------------------------------------------------------------------------------------------------
 *
 * Disable the original ProductMedia's Flickity-based functionality since we use Slyde.
 * The variant change handling is done in product-media.liquid script instead.
 */

(function() {
  // Patch ProductMedia to not error when flickity-carousel doesn't exist
  // The original ProductMedia class expects flickity-carousel and errors without it

  const patchProductMedia = () => {
    document.querySelectorAll('product-media').forEach(productMedia => {
      if (productMedia._slydePatched) return;
      productMedia._slydePatched = true;

      // Create a dummy mainCarousel object to prevent errors
      // The actual carousel functionality is handled by product-carousel element
      if (!productMedia.mainCarousel) {
        productMedia.mainCarousel = {
          childElementCount: 0,
          // Stub methods that ProductMedia might call
          flickityInstance: Promise.resolve({
            destroy: () => {},
            on: () => {},
            selectedElement: null,
            cells: [],
            options: { draggable: true },
            updateDraggable: () => {}
          }),
          reload: () => {},
          select: () => {},
          setDraggable: () => {},
          addEventListener: () => {},
          removeEventListener: () => {}
        };
      }
    });
  };

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchProductMedia);
  } else {
    patchProductMedia();
  }

  // Also observe for dynamically added elements
  const observer = new MutationObserver((mutations) => {
    let shouldPatch = false;
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && (node.matches?.('product-media') || node.querySelector?.('product-media'))) {
          shouldPatch = true;
        }
      });
    });
    if (shouldPatch) {
      requestAnimationFrame(patchProductMedia);
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

